/**
 * Sigma08 Carbon Design System Theme Configuration
 * Implements IBM Carbon Design System with Carbon Blue color scheme
 *
 * Color System:
 * - Carbon Blue 60 (#0f62fe): Primary brand color
 * - Carbon Yellow 30 (#f1c21b): Warning/active states
 * - Carbon Red 50 (#da1e28): Error states
 *
 * Typography:
 * - IBM Plex Font: Primary font family
 * - IBM Plex Sans: Fallback font
 */

// @ts-ignore - @carbon/themes types not available
import { white } from '@carbon/themes';

// Use Carbon's white theme with minimal brand color overrides only
export const sigma08Theme = {
  ...white,
  // Minimal brand color overrides - only essential branding
  interactive01: '#0f62fe', // Carbon Blue 60 - Primary buttons & links
  interactive02: '#0043ce', // Carbon Blue 70 for hover states
  backgroundBrand: '#0f62fe', // Carbon Blue 60 for brand backgrounds
  focus: '#0043ce', // Maintain brand focus color

  // Keep standard button colors with brand primary
  buttonPrimary: '#0f62fe', // Carbon Blue 60
  buttonPrimaryHover: '#0043ce', // Carbon Blue 70
  buttonPrimaryActive: '#002d9c', // Carbon Blue 80

  // Keep brand link colors
  link01: '#0043ce',
  linkPrimary: '#0043ce',
  linkPrimaryHover: '#002d9c',

  // Minimal brand interactive tokens
  interactive: '#0043ce',
};

// CSS custom properties for Carbon integration - Carbon Blue color scheme
// All critical Carbon CSS variables required for full compliance
export const carbonCSSVariables = {
  // Core interactive colors
  '--cds-interactive-01': '#0f62fe', // Carbon Blue 60
  '--cds-interactive-02': '#0043ce', // Carbon Blue 70
  '--cds-interactive-03': '#f1c21b', // Carbon Yellow 30
  '--cds-interactive-04': '#0f62fe', // Carbon Blue 60

  // Brand colors
  '--cds-brand-01': '#0f62fe', // Carbon Blue 60
  '--cds-brand-02': '#0043ce', // Carbon Blue 70
  '--cds-brand-03': '#f1c21b', // Carbon Yellow 30

  // Text colors
  '--cds-text-primary': '#161616', // Primary text
  '--cds-text-secondary': '#525252', // Secondary text
  '--cds-text-placeholder': '#a8a8a8', // Placeholder text
  '--cds-text-error': '#da1e28', // Carbon Red 50
  '--cds-text-helper': '#6f6f6f', // Helper text
  '--cds-text-inverse': '#ffffff', // Inverse text
  '--cds-text-on-color': '#ffffff', // Text on colored backgrounds

  // Background colors
  '--cds-background': '#ffffff', // Main background
  '--cds-background-inverse': '#393939', // Inverse background
  '--cds-background-brand': '#0f62fe', // Brand background
  '--cds-background-hover': 'rgba(141, 141, 141, 0.12)', // Hover background
  '--cds-background-active': 'rgba(141, 141, 141, 0.3)', // Active background
  '--cds-background-selected': 'rgba(141, 141, 141, 0.2)', // Selected background

  // Layer colors (surfaces)
  '--cds-layer-01': '#f4f4f4', // Layer 1
  '--cds-layer-02': '#ffffff', // Layer 2
  '--cds-layer-03': '#f4f4f4', // Layer 3
  '--cds-layer-hover-01': '#e8e8e8', // Layer 1 hover
  '--cds-layer-hover-02': '#e8e8e8', // Layer 2 hover
  '--cds-layer-hover-03': '#e8e8e8', // Layer 3 hover
  '--cds-layer-active-01': '#c6c6c6', // Layer 1 active
  '--cds-layer-active-02': '#c6c6c6', // Layer 2 active
  '--cds-layer-active-03': '#c6c6c6', // Layer 3 active

  // Field colors (form inputs)
  '--cds-field-01': '#f4f4f4', // Field background
  '--cds-field-02': '#ffffff', // Field background alternate
  '--cds-field-03': '#f4f4f4', // Field background tertiary
  '--cds-field-hover-01': '#e8e8e8', // Field hover
  '--cds-field-hover-02': '#e8e8e8', // Field hover alternate
  '--cds-field-hover-03': '#e8e8e8', // Field hover tertiary

  // Border colors
  '--cds-border-subtle-00': '#e0e0e0', // Subtle border
  '--cds-border-subtle-01': '#c6c6c6', // Subtle border alternate
  '--cds-border-subtle-02': '#e0e0e0', // Subtle border secondary
  '--cds-border-subtle-03': '#c6c6c6', // Subtle border tertiary
  '--cds-border-strong-01': '#8d8d8d', // Strong border
  '--cds-border-strong-02': '#8d8d8d', // Strong border alternate
  '--cds-border-strong-03': '#8d8d8d', // Strong border tertiary
  '--cds-border-interactive': '#0043ce', // Interactive border
  '--cds-border-inverse': '#161616', // Inverse border
  '--cds-border-disabled': '#c6c6c6', // Disabled border

  // Support colors (semantic states)
  '--cds-support-error': '#da1e28', // Carbon Red 50
  '--cds-support-warning': '#f1c21b', // Carbon Yellow 30
  '--cds-support-success': '#24a148', // Green for success
  '--cds-support-info': '#0043ce', // Blue for info

  // Button colors
  '--cds-button-primary': '#0f62fe', // Carbon Blue 60
  '--cds-button-primary-hover': '#0043ce', // Carbon Blue 70
  '--cds-button-primary-active': '#002d9c', // Carbon Blue 80
  '--cds-button-secondary': '#393939', // Secondary button
  '--cds-button-secondary-hover': '#474747', // Secondary hover
  '--cds-button-secondary-active': '#161616', // Secondary active
  '--cds-button-tertiary': '#0043ce', // Tertiary button
  '--cds-button-tertiary-hover': '#0353e9', // Tertiary hover
  '--cds-button-tertiary-active': '#002d9c', // Tertiary active
  '--cds-button-danger-primary': '#da1e28', // Danger primary
  '--cds-button-danger-secondary': '#da1e28', // Danger secondary
  '--cds-button-danger-hover': '#ba1b23', // Danger hover
  '--cds-button-danger-active': '#750e13', // Danger active
  '--cds-button-disabled': '#c6c6c6', // Disabled button

  // Focus and interaction
  '--cds-focus': '#0043ce', // Carbon Blue 70 for focus
  '--cds-focus-inset': '#ffffff', // Focus inset
  '--cds-focus-inverse': '#ffffff', // Inverse focus

  // Link colors
  '--cds-link-primary': '#0043ce', // Carbon Blue 70 for links
  '--cds-link-primary-hover': '#002d9c', // Carbon Blue 80 for hover
  '--cds-link-secondary': '#0043ce', // Secondary link
  '--cds-link-inverse': '#78a9ff', // Inverse link
  '--cds-link-visited': '#8a3ffc', // Visited link

  // Icon colors
  '--cds-icon-primary': '#161616', // Primary icon
  '--cds-icon-secondary': '#525252', // Secondary icon
  '--cds-icon-inverse': '#ffffff', // Inverse icon
  '--cds-icon-on-color': '#ffffff', // Icon on colored background
  '--cds-icon-disabled': 'rgba(22, 22, 22, 0.25)', // Disabled icon

  // Misc utilities
  '--cds-overlay': 'rgba(22, 22, 22, 0.5)', // Modal overlay
  '--cds-shadow': 'rgba(0, 0, 0, 0.3)', // Shadow
  '--cds-skeleton-background': '#e5e5e5', // Skeleton loading
  '--cds-skeleton-element': '#c6c6c6', // Skeleton element
  '--cds-toggle-off': '#8d8d8d', // Toggle off state
  '--cds-highlight': '#d9e9ff', // Highlight color

  // Typography
  '--cds-font-family-sans': "'IBM Plex Sans', 'Helvetica Neue', Arial, sans-serif",
  '--cds-font-family-mono':
    "'IBM Plex Mono', 'Menlo', 'DejaVu Sans Mono', 'Bitstream Vera Sans Mono', Courier, monospace",

  // Spacing tokens (essential for layout)
  '--cds-spacing-01': '0.125rem', // 2px
  '--cds-spacing-02': '0.25rem', // 4px
  '--cds-spacing-03': '0.5rem', // 8px
  '--cds-spacing-04': '0.75rem', // 12px
  '--cds-spacing-05': '1rem', // 16px - standard field spacing
  '--cds-spacing-06': '1.5rem', // 24px - section spacing
  '--cds-spacing-07': '2rem', // 32px
  '--cds-spacing-08': '2.5rem', // 40px
  '--cds-spacing-09': '3rem', // 48px - page margins
  '--cds-spacing-10': '4rem', // 64px
  '--cds-spacing-11': '5rem', // 80px
  '--cds-spacing-12': '6rem', // 96px
  '--cds-spacing-13': '10rem', // 160px

  // Layout tokens
  '--cds-layout-01': '1rem', // 16px
  '--cds-layout-02': '1.5rem', // 24px
  '--cds-layout-03': '2rem', // 32px
  '--cds-layout-04': '3rem', // 48px
  '--cds-layout-05': '4rem', // 64px
  '--cds-layout-06': '6rem', // 96px
  '--cds-layout-07': '10rem', // 160px

  // Container sizes
  '--cds-container-01': '1.5rem', // 24px
  '--cds-container-02': '2rem', // 32px
  '--cds-container-03': '2.5rem', // 40px
  '--cds-container-04': '3rem', // 48px
  '--cds-container-05': '4rem', // 64px

  // Size tokens for consistent sizing
  '--cds-size-sm': '2rem', // 32px - small touch target
  '--cds-size-md': '2.5rem', // 40px - medium touch target
  '--cds-size-lg': '3rem', // 48px - large touch target
  '--cds-size-xl': '4rem', // 64px - extra large touch target
};

// Educational platform specific tokens
export const educationalTokens = {
  // Learning progress indicators
  progressNotStarted: '#e0e0e0',
  progressInProgress: '#1890ff',
  progressCompleted: '#24a148',
  progressMastered: '#722ed1',

  // Grade/assessment colors
  gradeExcellent: '#24a148', // A grade
  gradeGood: '#52c41a', // B grade
  gradeAverage: '#ffc700', // C grade
  gradePoor: '#fa8c16', // D grade
  gradeFailing: '#e60054', // F grade

  // Educational role colors
  roleStudent: '#1890ff', // Blue for students
  roleParent: '#52c41a', // Green for parents
  roleTeacher: '#fa8c16', // Orange for teachers
  roleAdmin: '#722ed1', // Purple for administrators

  // Subject area colors for visual organization (Carbon Design System compliant)
  subjectMath: '#8a3ffc', // Carbon Purple 60 - P1 compliance fix
  subjectScience: '#24a148',
  subjectEnglish: '#e60054',
  subjectHistory: '#fa8c16',
  subjectArts: '#8a3ffc', // Carbon Purple 60 - P1 compliance fix
};

// Export main theme
export const sigma08CarbonTheme = sigma08Theme;

export default sigma08CarbonTheme;
